import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Euro, Trash2, FileText, Pause, CheckCircle, Play } from '../../icons/wp-icons';
import { zeiterfassungApi } from '../../api/zeiterfassungApi';

function ZeiterfassungListe({ terminId, onSelectForRechnung, refreshKey }) {
  const navigate = useNavigate();
  const [zeiterfassungen, setZeiterfassungen] = useState([]);
  const [zusammenfassung, setZusammenfassung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadZeiterfassungen = useCallback(async () => {
    try {
      setLoading(true);
      const data = await zeiterfassungApi.getByTermin(terminId);
      setZeiterfassungen(data.zeiterfassungen || []);
      setZusammenfassung(data.zusammenfassung);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Zeiterfassungen');
    } finally {
      setLoading(false);
    }
  }, [terminId]);

  useEffect(() => {
    loadZeiterfassungen();
  }, [loadZeiterfassungen, refreshKey]);

  const handleDelete = async (id) => {
    if (!confirm('Zeiterfassung wirklich löschen?')) return;

    try {
      await zeiterfassungApi.delete(id);
      loadZeiterfassungen();
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMinuten = (minuten) => {
    if (!minuten || minuten <= 0) return '0 Min';
    const stunden = Math.floor(minuten / 60);
    const restMinuten = minuten % 60;
    if (stunden === 0) return `${restMinuten} Min`;
    if (restMinuten === 0) return `${stunden} Std`;
    return `${stunden} Std ${restMinuten} Min`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'laufend':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <Play className="w-3 h-3" />
            Läuft
          </span>
        );
      case 'pausiert':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Pause className="w-3 h-3" />
            Pausiert
          </span>
        );
      case 'abgeschlossen':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Abgeschlossen
          </span>
        );
      default:
        return null;
    }
  };

  const berechneKosten = (zeit) => {
    if (!zeit.gesamtminuten || !zeit.stundensatz) return 0;
    const stunden = zeit.gesamtminuten / 60;
    return Math.round(stunden * zeit.stundensatz * 100) / 100;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
        {error}
      </div>
    );
  }

  if (zeiterfassungen.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        Noch keine Zeiterfassungen für diesen Termin.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Zusammenfassung */}
      {zusammenfassung && zusammenfassung.anzahl > 0 && (
        <div className="bg-primary-light rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span className="font-medium">Gesamt:</span>
                <span>{zusammenfassung.gesamtArbeitszeit}</span>
              </div>
              <div className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-primary" />
                <span className="font-medium">{zusammenfassung.gesamtKostenFormatiert}</span>
              </div>
            </div>
            <span className="text-sm text-gray-600">
              {zusammenfassung.anzahl} Eintra{zusammenfassung.anzahl === 1 ? 'g' : 'ge'}
            </span>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="space-y-3">
        {zeiterfassungen.map((zeit) => (
          <div
            key={zeit.id}
            className={`bg-white border rounded-lg p-4 ${
              zeit.inRechnungId ? 'border-green-200 bg-green-50/30' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Header: Tätigkeit & Status */}
                <div className="flex items-center gap-2 mb-2">
                  {getStatusBadge(zeit.status)}
                  <span className="font-medium">
                    {zeit.katalogPosition?.bezeichnung || 'Arbeitszeit'}
                  </span>
                </div>

                {/* Notiz */}
                {zeit.notiz && (
                  <p className="text-sm text-gray-600 mb-2">{zeit.notiz}</p>
                )}

                {/* Details */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                  {/* Datum & Zeit */}
                  <span>
                    {formatTime(zeit.startzeit)}
                    {zeit.endzeit && ` - ${formatTime(zeit.endzeit)}`}
                  </span>

                  {/* Arbeitszeit */}
                  {zeit.status === 'abgeschlossen' && (
                    <>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatMinuten(zeit.gesamtminuten)}
                      </span>

                      {/* Pausenzeit */}
                      {zeit.pausenminuten > 0 && (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <Pause className="w-3 h-3" />
                          {formatMinuten(zeit.pausenminuten)} Pause
                        </span>
                      )}

                      {/* Kosten */}
                      <span className="flex items-center gap-1">
                        <Euro className="w-3 h-3" />
                        {berechneKosten(zeit).toFixed(2)} €
                      </span>
                    </>
                  )}

                  {/* Stundensatz */}
                  <span className="text-gray-400">
                    ({zeit.stundensatz.toFixed(2)} €/Std)
                  </span>
                </div>

                {/* In Rechnung Badge - klickbar */}
                {zeit.inRechnungId && zeit.inRechnung && (
                  <button
                    onClick={() => {
                      // Bei Entwurf zur Bearbeitung, sonst zur Übersicht
                      if (zeit.inRechnung.status === 'entwurf') {
                        navigate(`/rechnungen/${zeit.inRechnungId}/bearbeiten`);
                      } else {
                        navigate(`/rechnungen?id=${zeit.inRechnungId}`);
                      }
                    }}
                    className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition cursor-pointer"
                    title={zeit.inRechnung.status === 'entwurf' ? 'Rechnung bearbeiten' : 'Zur Rechnung'}
                  >
                    <FileText className="w-3 h-3" />
                    In Rechnung {zeit.inRechnung.rechnungsnummer}
                  </button>
                )}
              </div>

              {/* Aktionen */}
              <div className="flex items-center gap-2 ml-4">
                {/* In Rechnung übernehmen - nur wenn abgeschlossen und noch nicht in Rechnung */}
                {zeit.status === 'abgeschlossen' && !zeit.inRechnungId && onSelectForRechnung && (
                  <button
                    onClick={() => onSelectForRechnung(zeit)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark transition"
                  >
                    <FileText className="w-4 h-4" />
                    In Rechnung
                  </button>
                )}

                {/* Löschen - nur wenn nicht in Rechnung */}
                {!zeit.inRechnungId && (
                  <button
                    onClick={() => handleDelete(zeit.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ZeiterfassungListe;
